/* =====================================================================
 * SkillModal — 스킬 관리 모달
 *
 * 탭: "내 스킬" | "스킬 레퍼런스" | "스킬 가이드"
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import type { SkillSummary } from '@auw/api-client';
import {
  Plus,
  Trash2,
  FileText,
  Check,
  BookOpen,
  FolderOpen,
  FolderArchive,
  Sparkles,
  AlertCircle,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Code,
  BarChart3,
  Image,
  Megaphone,
  Search,
  Languages,
  Workflow,
  GraduationCap,
  Presentation,
  Star,
  Pencil,
  Loader2,
  ArrowLeft,
  MessageSquare,
  Copy,
  X,
} from 'lucide-react';
import { Modal } from '@auw/workspace-ui';
import { Tabs, type TabItem } from '@auw/workspace-ui';
import { Chip } from '@auw/workspace-ui';
import { useSkillStore } from '@auw/stores';
import { useReboModeStore } from '@auw/stores';
import { useOfficialSkillStore } from '@auw/stores';
import type { OfficialSkillSummary } from '@auw/api-client';
import { useOfficialSkillThumbnail } from '@auw/api-client';
import { resolveSkillReferenceLogo, useTenant } from '@auw/tenant';

import { resolveLocalized, resolveLocalizedArray } from '@auw/workspace-utils';

/**
 * Default reference-card hero used when the active tenant doesn't
 * declare `assets.skills.referenceCardLogo`. Resolved through
 * `resolveSkillReferenceLogo()` so a tenant override automatically
 * takes precedence on every render.
 */
const DEFAULT_SKILL_REF_LOGO = '/favicon-128.png';

type RefCategory = {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  skill: { id: string; nameKey: string; descKey: string; tags: string[] };
};

const REF_CATEGORIES: RefCategory[] = [
  { id: 'all', labelKey: 'skill.refCat.all', icon: <Sparkles size={14} />,
    skill: { id: '', nameKey: '', descKey: '', tags: [] } },
  { id: 'document', labelKey: 'skill.refCat.document', icon: <FileText size={14} />,
    skill: { id: 'ref-doc', nameKey: 'skill.refSkill.docName', descKey: 'skill.refSkill.docDesc', tags: ['skill.refTag.document', 'skill.refTag.report'] } },
  { id: 'presentation', labelKey: 'skill.refCat.presentation', icon: <Presentation size={14} />,
    skill: { id: 'ref-ppt', nameKey: 'skill.refSkill.pptName', descKey: 'skill.refSkill.pptDesc', tags: ['skill.refTag.ppt', 'skill.refTag.design'] } },
  { id: 'code', labelKey: 'skill.refCat.code', icon: <Code size={14} />,
    skill: { id: 'ref-code', nameKey: 'skill.refSkill.codeName', descKey: 'skill.refSkill.codeDesc', tags: ['skill.refTag.dev', 'skill.refTag.code'] } },
  { id: 'data', labelKey: 'skill.refCat.data', icon: <BarChart3 size={14} />,
    skill: { id: 'ref-data', nameKey: 'skill.refSkill.dataName', descKey: 'skill.refSkill.dataDesc', tags: ['skill.refTag.data', 'skill.refTag.analysis'] } },
  { id: 'image', labelKey: 'skill.refCat.image', icon: <Image size={14} />,
    skill: { id: 'ref-image', nameKey: 'skill.refSkill.imageName', descKey: 'skill.refSkill.imageDesc', tags: ['skill.refTag.image', 'skill.refTag.design'] } },
  { id: 'marketing', labelKey: 'skill.refCat.marketing', icon: <Megaphone size={14} />,
    skill: { id: 'ref-marketing', nameKey: 'skill.refSkill.marketingName', descKey: 'skill.refSkill.marketingDesc', tags: ['skill.refTag.marketing', 'skill.refTag.content'] } },
  { id: 'research', labelKey: 'skill.refCat.research', icon: <Search size={14} />,
    skill: { id: 'ref-research', nameKey: 'skill.refSkill.researchName', descKey: 'skill.refSkill.researchDesc', tags: ['skill.refTag.research', 'skill.refTag.analysis'] } },
  { id: 'translation', labelKey: 'skill.refCat.translation', icon: <Languages size={14} />,
    skill: { id: 'ref-trans', nameKey: 'skill.refSkill.transName', descKey: 'skill.refSkill.transDesc', tags: ['skill.refTag.translation', 'skill.refTag.multilang'] } },
  { id: 'automation', labelKey: 'skill.refCat.automation', icon: <Workflow size={14} />,
    skill: { id: 'ref-auto', nameKey: 'skill.refSkill.autoName', descKey: 'skill.refSkill.autoDesc', tags: ['skill.refTag.automation', 'skill.refTag.workflow'] } },
  { id: 'education', labelKey: 'skill.refCat.education', icon: <GraduationCap size={14} />,
    skill: { id: 'ref-edu', nameKey: 'skill.refSkill.eduName', descKey: 'skill.refSkill.eduDesc', tags: ['skill.refTag.education', 'skill.refTag.learning'] } },
];

function isZipSource(filename: string): boolean {
  return filename.toLowerCase().endsWith('.zip');
}

interface SkillModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: 'my' | 'ref' | 'guide';
  centerPaddingLeft?: number;
  centerPaddingRight?: number;
}

const SkillModal: React.FC<SkillModalProps> = ({
  open,
  onClose,
  initialTab = 'my',
  centerPaddingLeft = 0,
  centerPaddingRight = 0,
}) => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const {
    skills, activeSkillIds, isLoading, error,
    fetchSkills, uploadSkills, deleteSkill, toggleSkill,
  } = useSkillStore();

  const tabs: TabItem[] = [
    { id: 'my', label: t('skill.tabMy'), icon: <FolderOpen size={14} /> },
    { id: 'ref', label: t('skill.tabRef'), icon: <BookOpen size={14} /> },
    { id: 'guide', label: t('skill.tabGuide'), icon: <HelpCircle size={14} /> },
  ];

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      fetchSkills();
    }
  }, [open, initialTab, fetchSkills]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('skill.modalTitle')}
      titleIcon={<Sparkles size={18} />}
      width="1080px"
      bodyPadding="0"
      bodyOverflowY="hidden"
      centerPaddingLeft={centerPaddingLeft}
      centerPaddingRight={centerPaddingRight}
    >
      <Tabs items={tabs} activeId={activeTab} onChange={setActiveTab} />

      <TabContent>
        {activeTab === 'my' && (
          <MySkillsTab
            skills={skills}
            activeSkillIds={activeSkillIds}
            isLoading={isLoading}
            error={error}
            onUpload={uploadSkills}
            onDelete={deleteSkill}
            onToggle={toggleSkill}
            onClose={onClose}
          />
        )}
        {activeTab === 'ref' && (
          <ReferenceTab
            activeSkillIds={activeSkillIds}
            onToggle={toggleSkill}
            onClose={onClose}
          />
        )}
        {activeTab === 'guide' && <GuideTab />}
      </TabContent>
    </Modal>
  );
};

export default SkillModal;

/* ------------------------------------------------------------------ */
/* My Skills Tab                                                       */
/* ------------------------------------------------------------------ */

interface MySkillsTabProps {
  skills: SkillSummary[];
  activeSkillIds: string[];
  isLoading: boolean;
  error: string | null;
  onUpload: (files: File[]) => Promise<unknown>;
  onDelete: (id: string) => Promise<void>;
  onToggle: (id: string) => void;
  onClose: () => void;
}

const MySkillsTab: React.FC<MySkillsTabProps> = ({
  skills, activeSkillIds, isLoading, error,
  onUpload, onDelete, onToggle, onClose,
}) => {
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const { renameSkill } = useSkillStore();

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files).filter(
        (f) => f.name.endsWith('.md') || f.name.endsWith('.zip'),
      );
      if (!arr.length) return;
      await onUpload(arr);
    },
    [onUpload],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const startEdit = useCallback((skill: SkillSummary) => {
    setEditingId(skill.skill_id);
    setEditName(skill.name);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, []);

  const commitEdit = useCallback(async () => {
    if (editingId && editName.trim()) {
      await renameSkill(editingId, editName.trim());
    }
    setEditingId(null);
  }, [editingId, editName, renameSkill]);

  const { setMode } = useReboModeStore();
  const handleApply = useCallback((skillId: string) => {
    const willActivate = !activeSkillIds.includes(skillId);
    onToggle(skillId);
    setMode(willActivate ? 'skill' : 'auto');
    if (willActivate) onClose();
  }, [activeSkillIds, onToggle, setMode, onClose]);

  return (
    <ScrollArea>
      {error && (
        <ErrorBanner>
          <AlertCircle size={14} />
          {error}
        </ErrorBanner>
      )}

      <CardGrid>
        <UploadCard
          $over={isDragOver}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.zip"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <UploadIconZone>
            <UploadIconWrap>
              <Plus size={28} strokeWidth={1.5} />
            </UploadIconWrap>
          </UploadIconZone>
          <UploadInfoZone>
            <UploadLabel>{t('skill.uploadLabel')}</UploadLabel>
            <UploadHint>{t('skill.uploadHint')}</UploadHint>
          </UploadInfoZone>
        </UploadCard>

        {isLoading && (
          <SkeletonCard>
            <SkeletonIconArea />
            <SkeletonBody>
              <SkeletonLine $width="70%" $height="13px" />
              <SkeletonLine $width="90%" $height="10px" />
              <SkeletonLine $width="40%" $height="10px" />
            </SkeletonBody>
          </SkeletonCard>
        )}

        {!isLoading && skills.map((skill) => {
          const isActive = activeSkillIds.includes(skill.skill_id);
          const isZip = isZipSource(skill.source_filename);
          const isEditing = editingId === skill.skill_id;
          return (
            <SkillCard
              key={skill.skill_id}
              $active={isActive}
            >
              <CardIconArea $active={isActive}>
                {isActive ? (
                  <Check size={28} strokeWidth={2} />
                ) : isZip ? (
                  <FolderArchive size={28} strokeWidth={1.5} />
                ) : (
                  <FileText size={28} strokeWidth={1.5} />
                )}
              </CardIconArea>
              <CardBody>
                {isEditing ? (
                  <EditNameInput
                    ref={editInputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitEdit();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <CardName>{skill.name}</CardName>
                )}
                {skill.description && <CardDesc>{skill.description}</CardDesc>}
                <CardMetaRow>
                  <CardMeta>
                    {skill.has_references && <Chip size="sm" variant="subtle">references</Chip>}
                    {skill.has_assets && <Chip size="sm" variant="subtle">assets</Chip>}
                    {skill.has_scripts && <Chip size="sm" variant="subtle">scripts</Chip>}
                    {!skill.has_references && !skill.has_assets && !skill.has_scripts && (
                      <CardFormat>{isZip ? '.zip' : '.md'}</CardFormat>
                    )}
                  </CardMeta>
                  <CardApplyBtn
                    $active={isActive}
                    onClick={(e) => { e.stopPropagation(); handleApply(skill.skill_id); }}
                  >
                    {isActive ? t('skill.deactivate') : t('skill.apply')}
                  </CardApplyBtn>
                </CardMetaRow>
              </CardBody>

              <CardTopActions className="top-actions">
                <ActionBtn
                  onClick={(e) => { e.stopPropagation(); startEdit(skill); }}
                  title={t('skill.rename')}
                >
                  <Pencil size={14} />
                </ActionBtn>
                <ActionBtn
                  onClick={(e) => { e.stopPropagation(); onDelete(skill.skill_id); }}
                  title={t('skill.delete')}
                >
                  <Trash2 size={14} />
                </ActionBtn>
              </CardTopActions>
            </SkillCard>
          );
        })}
      </CardGrid>

      {skills.length === 0 && !isLoading && (
        <EmptyState>{t('skill.empty')}</EmptyState>
      )}
    </ScrollArea>
  );
};

/* ------------------------------------------------------------------ */
/* Reference Tab (Mockup)                                              */
/* ------------------------------------------------------------------ */

interface ReferenceTabProps {
  activeSkillIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
}

const OUTPUT_FORMAT_LABELS: Record<string, string> = {
  auto: 'Auto',
  text: 'Text',
  image: 'Image',
  video: 'Video',
  docx: 'DOCX',
  pptx: 'PPTX',
  hwpx: 'HWPX',
  xlsx: 'XLSX',
  interactive_ui: 'Interactive UI',
};

const ReferenceTab: React.FC<ReferenceTabProps> = ({ activeSkillIds, onToggle, onClose }) => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language;
  const tenant = useTenant();
  /* Phase 12 — tenant assets:
   * Reference cards always render an image; missing tenant overrides
   * fall through to `/favicon-128.png` so B2C and any tenant that
   * leaves `assets.skills.referenceCardLogo` unset keeps the legacy
   * "Rebo" hero. */
  const skillRefLogo = useMemo(
    () => resolveSkillReferenceLogo(tenant, DEFAULT_SKILL_REF_LOGO),
    [tenant],
  );
  const [activeCat, setActiveCat] = useState('all');
  const [detailSkill, setDetailSkill] = useState<OfficialSkillSummary | null>(null);
  const chipBarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { favoriteRefIds, fetchFavorites, toggleFavorite } = useSkillStore();
  const { skills: officialSkills, isLoading: officialLoading, fetchPublicSkills } = useOfficialSkillStore();
  const { setMode } = useReboModeStore();
  const [copiedUc, setCopiedUc] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFavorites();
    fetchPublicSkills();
  }, [fetchFavorites, fetchPublicSkills]);

  const updateScrollFlags = useCallback(() => {
    const el = chipBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollFlags();
    const el = chipBarRef.current;
    if (el) el.addEventListener('scroll', updateScrollFlags, { passive: true });
    return () => el?.removeEventListener('scroll', updateScrollFlags);
  }, [updateScrollFlags]);

  const scrollChips = useCallback((dir: 'left' | 'right') => {
    chipBarRef.current?.scrollBy({ left: dir === 'right' ? 180 : -180, behavior: 'smooth' });
  }, []);

  const visibleSkills = useMemo(() => {
    let list: OfficialSkillSummary[] = officialSkills;
    if (activeCat !== 'all') {
      list = list.filter((s) => s.category === activeCat);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const localName = resolveLocalized(s.display_name, lang, s.name);
        const localDesc = resolveLocalized(s.display_description, lang, s.description);
        return (
          localName.toLowerCase().includes(q) ||
          localDesc.toLowerCase().includes(q) ||
          s.name.toLowerCase().includes(q)
        );
      });
    }
    return [...list].sort((a, b) => {
      const aFav = favoriteRefIds.includes(a.skill_id) ? 0 : 1;
      const bFav = favoriteRefIds.includes(b.skill_id) ? 0 : 1;
      return aFav - bFav;
    });
  }, [officialSkills, activeCat, favoriteRefIds, searchQuery, lang]);

  const catLookup = useMemo(() => {
    const map: Record<string, RefCategory> = {};
    for (const c of REF_CATEGORIES) map[c.id] = c;
    return map;
  }, []);

  const handleRefApply = useCallback((skillId: string) => {
    const willActivate = !activeSkillIds.includes(skillId);
    onToggle(skillId);
    setMode(willActivate ? 'skill' : 'auto');
    if (willActivate) onClose();
  }, [activeSkillIds, onToggle, setMode, onClose]);

  if (detailSkill) {
    const detailCat = catLookup[detailSkill.category];
    return (
      <RefTabWrap>
        <ScrollArea>
          <DetailWrap>
            <DetailBackBtn onClick={() => setDetailSkill(null)}>
              <ArrowLeft size={14} />
              {t('skill.detailBack', 'Back')}
            </DetailBackBtn>

            <DetailHeader>
              <RefAvatar src={skillRefLogo.src} alt={skillRefLogo.alt ?? 'Rebo'} style={{ width: 48, height: 48, borderRadius: 12 }} />
              <DetailHeaderText>
                <DetailTitle>{resolveLocalized(detailSkill.display_name, lang, detailSkill.name)}</DetailTitle>
                <DetailSubtext>{detailSkill.creator_name || detailSkill.creator_id || 'askyour.ai'}</DetailSubtext>
              </DetailHeaderText>
            </DetailHeader>

            <DetailGrid>
              <DetailItem>
                <DetailLabel>{t('skill.detailCreator', 'Creator')}</DetailLabel>
                <DetailValue>{detailSkill.creator_name || detailSkill.creator_id || 'askyour.ai'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('skill.detailCreatedAt', 'Created')}</DetailLabel>
                <DetailValue>
                  {detailSkill.created_at
                    ? new Date(detailSkill.created_at).toLocaleDateString(lang, {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                      })
                    : '-'}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('skill.detailCategory', 'Category')}</DetailLabel>
                <DetailValue>
                  {detailCat ? (
                    <Chip size="sm" variant="subtle">{t(detailCat.labelKey)}</Chip>
                  ) : (
                    detailSkill.category
                  )}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>{t('skill.detailOutputFormat', 'Output Format')}</DetailLabel>
                <DetailValue>
                  <Chip size="sm" variant="subtle">
                    {OUTPUT_FORMAT_LABELS[detailSkill.output_format] || detailSkill.output_format || 'Auto'}
                  </Chip>
                </DetailValue>
              </DetailItem>
            </DetailGrid>

            <DetailDescSection>
              <DetailLabel>{t('skill.detailDescription', 'Description')}</DetailLabel>
              <DetailDescText>
                {resolveLocalized(detailSkill.display_description, lang, detailSkill.description) || t('skill.noDescription', 'No description')}
              </DetailDescText>
            </DetailDescSection>

            {(() => {
              const cases = resolveLocalizedArray(detailSkill.use_cases, lang);
              if (!cases.length) return null;
              return (
                <UseCaseSection>
                  <DetailLabel>{t('skill.detailUseCases', 'Use Cases')}</DetailLabel>
                  <UseCaseList>
                    {cases.map((uc, i) => (
                      <UseCaseItem key={i}>
                        <MessageSquare size={14} />
                        <UseCaseText>{uc}</UseCaseText>
                        <UseCaseCopyBtn
                          onClick={() => {
                            navigator.clipboard.writeText(uc);
                            setCopiedUc(i);
                            setTimeout(() => setCopiedUc(null), 1500);
                          }}
                          title={t('skill.copy', 'Copy')}
                        >
                          {copiedUc === i ? <Check size={13} /> : <Copy size={13} />}
                        </UseCaseCopyBtn>
                      </UseCaseItem>
                    ))}
                  </UseCaseList>
                </UseCaseSection>
              );
            })()}
          </DetailWrap>
        </ScrollArea>
      </RefTabWrap>
    );
  }

  return (
    <RefTabWrap>
      <CatBarWrap>
        {canScrollLeft && (
          <CatFade $side="left" />
        )}
        {canScrollLeft && (
          <CatArrow $side="left" onClick={() => scrollChips('left')}>
            <ChevronLeft size={16} />
          </CatArrow>
        )}
        <CatChipBar ref={chipBarRef}>
          {REF_CATEGORIES.map((cat) => (
            <CatChip
              key={cat.id}
              $active={activeCat === cat.id}
              onClick={() => setActiveCat(cat.id)}
            >
              {cat.icon}
              <span>{t(cat.labelKey)}</span>
            </CatChip>
          ))}
        </CatChipBar>
        {canScrollRight && (
          <CatFade $side="right" />
        )}
        {canScrollRight && (
          <CatArrow $side="right" onClick={() => scrollChips('right')}>
            <ChevronRight size={16} />
          </CatArrow>
        )}
      </CatBarWrap>

      <RefSearchWrap>
        <Search size={14} />
        <RefSearchInput
          type="text"
          placeholder={t('skill.searchPlaceholder', 'Search skills...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <RefSearchClear onClick={() => setSearchQuery('')}>
            <X size={12} />
          </RefSearchClear>
        )}
      </RefSearchWrap>

      <ScrollArea>
        {officialLoading ? (
          <RefLoadingWrap>
            <RefSpinner size={18} />
          </RefLoadingWrap>
        ) : visibleSkills.length === 0 ? (
          <RefEmptyWrap>
            <Sparkles size={24} />
            <span>{t('skill.refEmpty')}</span>
          </RefEmptyWrap>
        ) : (
          <CardGrid>
            {visibleSkills.map((skill) => (
              <RefSkillCard
                key={skill.skill_id}
                skill={skill}
                category={catLookup[skill.category]}
                isFav={favoriteRefIds.includes(skill.skill_id)}
                isActive={activeSkillIds.includes(skill.skill_id)}
                fallbackLogo={skillRefLogo}
                onApply={() => handleRefApply(skill.skill_id)}
                onDetail={() => setDetailSkill(skill)}
                onToggleFav={() => toggleFavorite(skill.skill_id)}
              />
            ))}
          </CardGrid>
        )}
      </ScrollArea>
    </RefTabWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Reference Skill Card (My Skills-style: thumbnail + body + actions)  */
/* ------------------------------------------------------------------ */

function useInViewOnce<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  return [ref, inView];
}

interface RefSkillCardProps {
  skill: OfficialSkillSummary;
  category?: RefCategory;
  isFav: boolean;
  isActive: boolean;
  fallbackLogo: { src: string; alt?: string };
  onApply: () => void;
  onDetail: () => void;
  onToggleFav: () => void;
}

const RefSkillCard: React.FC<RefSkillCardProps> = ({
  skill,
  category,
  isFav,
  isActive,
  fallbackLogo,
  onApply,
  onDetail,
  onToggleFav,
}) => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language;
  const [cardRef, inView] = useInViewOnce<HTMLDivElement>();

  const thumbUrl = useOfficialSkillThumbnail({
    skillId: skill.skill_id,
    hasThumbnail: !!skill.has_thumbnail,
    version: skill.thumbnail_version ?? 0,
    enabled: inView,
  });

  const focus = skill.thumbnail_focus ?? { x: 0.5, y: 0.5 };
  const objectPosition = `${(focus.x ?? 0.5) * 100}% ${(focus.y ?? 0.5) * 100}%`;

  return (
    <RefCardNew ref={cardRef}>
      <RefThumb>
        {skill.has_thumbnail && thumbUrl ? (
          <RefThumbImg
            src={thumbUrl}
            alt={resolveLocalized(skill.display_name, lang, skill.name)}
            style={{ objectPosition }}
            loading="lazy"
          />
        ) : (
          <RefThumbFallbackImg src={fallbackLogo.src} alt={fallbackLogo.alt ?? 'Rebo'} />
        )}
        {/* 즐겨찾기는 썸네일 우상단 오버레이로 띄워 좁은 카드에서도
         * 하단 액션 버튼(적용/상세)과 겹치지 않게 한다. */}
        <FavBtn
          $active={isFav}
          onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
          title={t('skill.favorite')}
        >
          <Star size={15} fill={isFav ? 'currentColor' : 'none'} />
        </FavBtn>
      </RefThumb>

      <RefCardBody>
        <RefSkillName>{resolveLocalized(skill.display_name, lang, skill.name)}</RefSkillName>
        <RefCreator>{skill.creator_name || skill.creator_id || 'askyour.ai'}</RefCreator>
        <RefDesc>{resolveLocalized(skill.display_description, lang, skill.description)}</RefDesc>
        <RefCatLabel>
          {category && <Chip size="sm" variant="subtle">{t(category.labelKey)}</Chip>}
        </RefCatLabel>
      </RefCardBody>

      <RefCardFooter>
        <RefApplyBtn
          $active={isActive}
          onClick={(e) => { e.stopPropagation(); onApply(); }}
        >
          {isActive ? t('skill.deactivate') : t('skill.apply')}
        </RefApplyBtn>
        <RefDetailBtn
          onClick={(e) => { e.stopPropagation(); onDetail(); }}
        >
          {t('skill.detail', '상세보기')}
        </RefDetailBtn>
      </RefCardFooter>
    </RefCardNew>
  );
};

/* ------------------------------------------------------------------ */
/* Guide Tab                                                           */
/* ------------------------------------------------------------------ */

const GuideTab: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <ScrollArea>
      <GuideContent>
        <GuideSection>
          <GuideH2>{t('skill.guide.overviewTitle')}</GuideH2>
          <GuideP>{t('skill.guide.overviewBody')}</GuideP>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.structureTitle')}</GuideH2>
          <GuideP>{t('skill.guide.structureDesc')}</GuideP>
          <GuideCode>{`my-skill/
├── SKILL.md            # ${t('skill.guide.structureSkillMd')}
├── references/         # ${t('skill.guide.structureRefs')}
├── assets/             # ${t('skill.guide.structureAssets')}
└── scripts/            # ${t('skill.guide.structureScripts')}`}</GuideCode>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.frontmatterTitle')}</GuideH2>
          <GuideP>{t('skill.guide.frontmatterWhat')}</GuideP>
          <GuideCode>{`---
name: my-skill
description: ${t('skill.guide.formatExampleDesc')}
metadata:
  author: your-name
  version: "1.0"
---

${t('skill.guide.formatExampleBody')}`}</GuideCode>
          <GuideP>{t('skill.guide.frontmatterExplain')}</GuideP>
          <GuideCallout>{t('skill.guide.frontmatterOptional')}</GuideCallout>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.fieldsTitle')}</GuideH2>
          <GuideTable>
            <thead>
              <tr>
                <GuideTh>{t('skill.guide.fieldCol')}</GuideTh>
                <GuideTh>{t('skill.guide.requiredCol')}</GuideTh>
                <GuideTh>{t('skill.guide.descCol')}</GuideTh>
              </tr>
            </thead>
            <tbody>
              <tr>
                <GuideTd><GuideInlineCode>name</GuideInlineCode></GuideTd>
                <GuideTd>{t('skill.guide.optional')}</GuideTd>
                <GuideTd>{t('skill.guide.fieldName')}</GuideTd>
              </tr>
              <tr>
                <GuideTd><GuideInlineCode>description</GuideInlineCode></GuideTd>
                <GuideTd>{t('skill.guide.optional')}</GuideTd>
                <GuideTd>{t('skill.guide.fieldDescription')}</GuideTd>
              </tr>
              <tr>
                <GuideTd><GuideInlineCode>metadata</GuideInlineCode></GuideTd>
                <GuideTd>{t('skill.guide.optional')}</GuideTd>
                <GuideTd>{t('skill.guide.fieldMetadata')}</GuideTd>
              </tr>
            </tbody>
          </GuideTable>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.foldersTitle')}</GuideH2>

          <GuideH3>{t('skill.guide.refsFolderTitle')}</GuideH3>
          <GuideP>{t('skill.guide.refsFolderDesc')}</GuideP>
          <GuideP>{t('skill.guide.refsFolderExts')}</GuideP>

          <GuideH3>{t('skill.guide.assetsFolderTitle')}</GuideH3>
          <GuideP>{t('skill.guide.assetsFolderDesc')}</GuideP>
          <GuideP>{t('skill.guide.assetsFolderExts')}</GuideP>

          <GuideH3>{t('skill.guide.scriptsFolderTitle')}</GuideH3>
          <GuideP>{t('skill.guide.scriptsFolderDesc')}</GuideP>
          <GuideP>{t('skill.guide.scriptsFolderExts')}</GuideP>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.uploadTitle')}</GuideH2>
          <GuideOl>
            <li>{t('skill.guide.uploadStep1')}</li>
            <li>{t('skill.guide.uploadStep2')}</li>
            <li>{t('skill.guide.uploadStep3')}</li>
          </GuideOl>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.activateTitle')}</GuideH2>
          <GuideP>{t('skill.guide.activateBody')}</GuideP>
        </GuideSection>

        <GuideSection>
          <GuideH2>{t('skill.guide.zipTitle')}</GuideH2>
          <GuideP>{t('skill.guide.zipDesc')}</GuideP>
          <GuideCode>{`my-skill.zip/
├── instructions.md     # ${t('skill.guide.zipRootMd')}
├── references/
│   └── guide.md
├── assets/
│   └── logo.png
└── scripts/
    └── setup.sh`}</GuideCode>
          <GuideCallout>{t('skill.guide.zipNote')}</GuideCallout>
        </GuideSection>
      </GuideContent>
    </ScrollArea>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const TabContent = styled.div`
  flex: 1;
  height: min(680px, 75vh);
  max-height: min(680px, 75vh);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 600px) {
    height: 380px;
    max-height: 380px;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 28px 28px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 14px 14px 18px;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  color: #ef4444;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.06)'};
`;

const EmptyState = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Card Grid ---- */

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

/* ---- Upload Card (DropZone) ---- */

const UploadCard = styled.div<{ $over: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1.5px dashed ${({ theme, $over }) =>
    $over ? theme.colors.accent : theme.colors.border};
  background: ${({ theme, $over }) =>
    $over
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
  }
`;

const UploadIconZone = styled.div`
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadInfoZone = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const UploadIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const UploadLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const UploadHint = styled.span`
  font-size: 0.6875rem;
  opacity: 0.55;
  text-align: center;
`;

/* ---- Skill Card ---- */

const SkillCard = styled.div<{ $active: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.3)'
        : '0 4px 16px rgba(0,0,0,0.06)'};
  }

  &:hover .top-actions { opacity: 1; }
`;

const CardIconArea = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  background: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)'
      : theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const CardBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const CardName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardDesc = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
`;

const CardFormat = styled.span`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.6;
`;

const CardTopActions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};
  z-index: 3;
`;

const ActionBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.85)'};
  backdrop-filter: blur(4px);
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)'};
  }

  &:last-child:hover {
    color: #ef4444;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.1)'};
  }
`;

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
`;

const CardApplyBtn = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.625rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.textMuted};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  }
`;

const EditNameInput = styled.input`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: transparent;
  border: none;
  border-bottom: 1.5px solid ${({ theme }) => theme.colors.accent};
  outline: none;
  padding: 0 0 2px;
  width: 100%;
  line-height: 1.3;
`;

/* ---- Ref Tab Layout ---- */

const RefTabWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const refSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RefSpinner = styled(Loader2)`
  animation: ${refSpin} 1s linear infinite;
`;

const RefLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const RefEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

/* ---- Category Chip Bar ---- */

const CatBarWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px 28px 12px;
  flex-shrink: 0;
`;

const CatChipBar = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 2px 4px;
  flex: 1;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CatChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: all ${({ theme }) => theme.transitions.fast};
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.border};
  background: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)'
      : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.textSecondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const CatFade = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  ${({ $side }) => ($side === 'left' ? 'left: 28px;' : 'right: 28px;')}
  top: 0;
  bottom: 0;
  width: 40px;
  z-index: 1;
  pointer-events: none;
  background: ${({ $side, theme }) => {
    const bg = theme.mode === 'dark' ? '30,30,30' : '255,255,255';
    return $side === 'left'
      ? `linear-gradient(to right, rgba(${bg},1) 0%, rgba(${bg},0) 100%)`
      : `linear-gradient(to left, rgba(${bg},1) 0%, rgba(${bg},0) 100%)`;
  }};
`;

const CatArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  ${({ $side }) => ($side === 'left' ? 'left: 28px;' : 'right: 28px;')}
  z-index: 2;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.08)'};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

/* ---- Ref Card (New Design) ---- */

const RefCardNew = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.3)'
        : '0 4px 16px rgba(0,0,0,0.06)'};
  }
`;

const RefThumb = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(255,255,255,0.04))'
      : 'linear-gradient(135deg, rgba(99,102,241,0.10), rgba(0,0,0,0.025))'};

  /* 모바일 2열 카드는 폭이 좁아 4:3 썸네일이 세로로 과하게 길어진다.
   * 16:9 로 낮춰 카드 전체 높이를 줄이고 넓이/높이 균형을 맞춘다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    aspect-ratio: 16 / 9;
  }
`;

const RefThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const RefThumbFallbackImg = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  object-fit: cover;
  opacity: 0.92;
`;

const RefAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
`;

const RefSkillName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RefCreator = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.3;
`;

const RefCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  padding: 12px 14px 10px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 3px;
    padding: 9px 10px 8px;
  }
`;

const RefDesc = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(0.6875rem * 1.45 * 2);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.625rem;
    -webkit-line-clamp: 1;
    min-height: 0;
  }
`;

const RefCatLabel = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 6px;
`;

const RefCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px 12px;
  border-top: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 5px;
    padding: 7px 10px 10px;
  }
`;

const RefApplyBtn = styled.button<{ $active: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 0.625rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textSecondary : theme.colors.textInverse};
  background: ${({ $active, theme }) =>
    $active ? 'transparent' : theme.colors.accent};
  border: 1px solid ${({ $active, theme }) =>
    $active ? theme.colors.border : theme.colors.accent};

  &:hover {
    opacity: ${({ $active }) => ($active ? 1 : 0.88)};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.textPrimary : theme.colors.textInverse};
    border-color: ${({ $active, theme }) =>
      $active ? theme.colors.textMuted : theme.colors.accent};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.5625rem;
    padding: 5px 6px;
  }
`;

const RefDetailBtn = styled.button`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 0.625rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.5625rem;
    padding: 5px 6px;
  }
`;

/* 즐겨찾기 버튼 — 썸네일 우상단 오버레이. 좁은 카드에서 하단 액션과
 * 겹치지 않도록 footer 밖으로 분리했다. */
const FavBtn = styled.button<{ $active: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ $active }) => ($active ? '#f59e0b' : '#ffffff')};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.42)' : 'rgba(0,0,0,0.32)'};
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  &:hover {
    color: #f59e0b;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.45)'};
  }
`;

/* ---- Skill Detail View ---- */

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 560px;
  margin: 0 auto;
`;

const DetailBackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  align-self: flex-start;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const DetailHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DetailTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.3;
`;

const DetailSubtext = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
`;

const DetailDescSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
`;

const DetailDescText = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  white-space: pre-wrap;
`;

const UseCaseSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UseCaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UseCaseItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;

  > svg:first-child {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.accent};
    opacity: 0.7;
  }
`;

const UseCaseText = styled.span`
  flex: 1;
  min-width: 0;
`;

const UseCaseCopyBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RefSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin: 0 28px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
  color: ${({ theme }) => theme.colors.textMuted};

  > svg:first-child { flex-shrink: 0; opacity: 0.5; }
`;

const RefSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};

  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const RefSearchClear = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---- Skeleton Card ---- */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const SkeletonIconArea = styled.div`
  aspect-ratio: 4 / 3;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)'
      : 'linear-gradient(90deg, rgba(0,0,0,0.025) 0%, rgba(0,0,0,0.055) 50%, rgba(0,0,0,0.025) 100%)'};
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

const SkeletonBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonLine = styled.div<{ $width: string; $height: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: 4px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.06) 100%)'
      : 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.07) 50%, rgba(0,0,0,0.04) 100%)'};
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

/* ---- Guide Tab Styles ---- */

const GuideContent = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const GuideSection = styled.section`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GuideH2 = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 10px;
  line-height: 1.4;
`;

const GuideH3 = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 16px 0 6px;
  line-height: 1.4;

  &:first-child {
    margin-top: 0;
  }
`;

const GuideP = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin: 0 0 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GuideCode = styled.pre`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.75rem;
  line-height: 1.65;
  padding: 14px 16px;
  border-radius: 10px;
  margin: 8px 0 12px;
  overflow-x: auto;
  white-space: pre;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const GuideInlineCode = styled.code`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const GuideTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  margin: 8px 0 12px;
`;

const GuideTh = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const GuideTd = styled.td`
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const GuideOl = styled.ol`
  margin: 4px 0 0;
  padding-left: 20px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.8;
`;

const GuideCallout = styled.div`
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.75rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'};
  border-left: 3px solid ${({ theme }) => theme.colors.accent};
`;
